/* RelaxPals: soft cross-fades on top of Horizon (uploaded by `deploy.py theme`, included from layout/theme.liquid).
   1. Product cards: Horizon jumps to the second photo on hover. A still copy of the outgoing photo is laid over the card and
      dissolved once the incoming photo is ready, so the swap reads as a cross-fade.
   2. Product page and quick add: picking a colour or pack replaces the whole gallery. The outgoing photo is held over the new
      gallery the same way and dissolved into the new colour.
   3. Homepage hero video: the theme renders the desktop and the phone video without autoplay and with preload="none"; this starts
      only the one on screen (again when the window crosses the phone breakpoint), adds a pause/play button (autoplaying motion
      needs one) and starts nothing for visitors who ask for reduced motion: they see the poster until they press play.
   4. Product page video strip (blocks/rp-reel.liquid): the cards drift sideways (the list plus a copy of it, moved by a
      transform), can be dragged, and load and play only while they are on screen. A tap opens the videos full screen one after
      another, with progress bars and an add-to-cart button that presses the page's own. Under reduced motion nothing drifts or
      plays by itself: the strip scrolls by hand and shows posters until a card is opened. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FADE_MS = 450;

  /** Lay a still copy of `img` exactly over it, inside `host` (a positioned ancestor). */
  function ghost(img, host) {
    if (!img || !host || !img.complete || !img.naturalWidth) return null;
    const ir = img.getBoundingClientRect();
    const hr = host.getBoundingClientRect();
    if (ir.width < 2 || ir.height < 2) return null;
    const cs = getComputedStyle(img);
    const copy = document.createElement('img');
    copy.className = 'rp-ghost';
    copy.alt = '';
    copy.setAttribute('aria-hidden', 'true');
    copy.decoding = 'sync';
    copy.src = img.currentSrc || img.src;
    Object.assign(copy.style, {
      left: `${ir.left - hr.left - host.clientLeft}px`,
      top: `${ir.top - hr.top - host.clientTop}px`,
      width: `${ir.width}px`,
      height: `${ir.height}px`,
      objectFit: cs.objectFit,
      objectPosition: cs.objectPosition,
      borderRadius: cs.borderRadius,
    });
    host.appendChild(copy);
    return copy;
  }

  /** Resolves once `img` has pixels (or after `timeout` ms). */
  function ready(img, timeout) {
    if (!img || (img.complete && img.naturalWidth)) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, timeout);
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  function dissolve(copy, when) {
    when.then(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          copy.style.opacity = '0';
          setTimeout(() => copy.remove(), FADE_MS + 150);
        })
      )
    );
  }

  // 1. product cards ---------------------------------------------------------------------------
  function cardImage(gallery) {
    const show = gallery.querySelector('slideshow-component');
    const slide = show && show.slides && typeof show.current === 'number' ? show.slides[show.current] : null;
    return (slide || gallery).querySelector('img');
  }

  function onCardPointer(event) {
    if (reduce.matches || event.pointerType !== 'mouse') return;
    const gallery = event.target;
    if (!(gallery instanceof Element) || !gallery.classList.contains('card-gallery')) return;
    if (!gallery.hasAttribute('on:pointerenter')) return; // single-photo cards never swap
    const copy = ghost(cardImage(gallery), gallery.querySelector('slideshow-container') || gallery);
    if (!copy) return;
    // capture phase on window runs before Horizon's own hover handler, which swaps the slide synchronously after us
    requestAnimationFrame(() => dissolve(copy, ready(cardImage(gallery), 800)));
  }

  window.addEventListener('pointerenter', onCardPointer, true);
  window.addEventListener('pointerleave', onCardPointer, true);

  // 2. product page / quick add gallery ---------------------------------------------------------
  function firstVisibleImage(root) {
    for (const img of root.querySelectorAll('img')) {
      const r = img.getBoundingClientRect();
      if (r.width > 40 && r.height > 40 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth) {
        return img;
      }
    }
    return null;
  }

  document.addEventListener(
    'change',
    (event) => {
      if (reduce.matches) return;
      const input = event.target;
      if (!(input instanceof Element) || !input.closest('variant-picker')) return;
      const scope = input.closest('.shopify-section, dialog');
      const gallery = scope && scope.querySelector('media-gallery');
      const host = gallery && gallery.parentElement;
      if (!host) return;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      const copy = ghost(firstVisibleImage(gallery), host);
      if (!copy) return;
      copy.classList.add('rp-ghost--over'); // media-gallery is sticky with its own z-index; the copy must sit above it
      const started = performance.now();
      (function waitForNewGallery() {
        const current = host.querySelector(':scope > media-gallery');
        if (current && current !== gallery) {
          dissolve(copy, ready(firstVisibleImage(current) || current.querySelector('img'), 1500));
        } else if (performance.now() - started > 4000) {
          copy.remove();
        } else {
          requestAnimationFrame(waitForNewGallery);
        }
      })();
    },
    true
  );
})();

(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');
  document.querySelectorAll('.hero__container').forEach((hero) => {
    const wraps = [...hero.querySelectorAll('[data-rp-hero-video]')];
    const videos = wraps.map((w) => w.querySelector('video')).filter(Boolean);
    if (!videos.length) return;
    const labels = wraps[0].dataset;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rp-hero-toggle';
    const visible = () => videos.filter((v) => v.offsetParent !== null);
    const show = () => {
      const playing = visible().some((v) => !v.paused);
      button.dataset.state = playing ? 'playing' : 'paused';
      button.setAttribute('aria-label', playing ? labels.rpPause : labels.rpPlay);
    };
    const start = () => {
      videos.forEach((v) => {
        const on = v.offsetParent !== null;
        if (on && v.paused && !hero.dataset.rpStopped) v.play().catch(show);
        if (!on && !v.paused) v.pause();
      });
      show();
    };
    button.addEventListener('click', () => {
      if (visible().some((v) => !v.paused)) {
        hero.dataset.rpStopped = '1';
        videos.forEach((v) => v.pause());
      } else {
        delete hero.dataset.rpStopped;
        start();
      }
    });
    videos.forEach((v) => {
      v.addEventListener('play', show);
      v.addEventListener('pause', show);
    });
    hero.appendChild(button);
    if (reduce.matches) hero.dataset.rpStopped = '1';
    phone.addEventListener('change', start);
    start();
  });
})();

// 4. product page video strip -----------------------------------------------------------------
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SPEED = 24; // drift, px per second
  const MIN_VIEW_S = 4; // in the viewer a short clip plays again until it has run this long

  class RpReel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.viewport = this.querySelector('.rp-reel__viewport');
      this.track = this.querySelector('.rp-reel__track');
      this.dialog = this.querySelector('.rp-reel__viewer');
      this.big = this.querySelector('.rp-reel__big');
      if (!this.track || !this.dialog || !this.big) return;
      this.ready = true;
      this.offset = 0;
      this.hold = 0;
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.drift = !reduce.matches && this.originals().length > 1;
      this.classList.toggle('rp-reel--drift', this.drift);
      this.listen();
      this.seen = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.onScreen ? this.start() : this.stop();
      }, { rootMargin: '120px 0px' });
      this.seen.observe(this);
      // the cards are small files: fetch every original one while the strip is still a screen away, so a card never
      // enters the strip before its video is there (on a phone the lazy fetch could lag behind the drift)
      this.near = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.warm();
        this.near.disconnect();
      }, { rootMargin: '900px 0px' });
      this.near.observe(this);
      this.onVisibility = () => (document.hidden ? this.stop() : this.onScreen && this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stop();
      this.seen?.disconnect();
      this.near?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.ready = false;
    }

    warm() {
      for (const li of this.originals()) {
        const video = li.querySelector('video');
        if (video && !video.getAttribute('src')) {
          video.preload = 'auto';
          video.src = video.dataset.src;
        }
      }
    }

    originals() {
      return [...this.track.children].filter((li) => !li.hasAttribute('data-rp-clone'));
    }

    cards() {
      return this.originals().map((li) => li.querySelector('.rp-reel__card'));
    }

    // a second copy of the list follows the first, so the drift can wrap without a gap
    clone() {
      if (!this.drift || this.track.querySelector('[data-rp-clone]')) return;
      for (const li of this.originals()) {
        const copy = li.cloneNode(true);
        copy.setAttribute('data-rp-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('button').forEach((b) => (b.tabIndex = -1));
        copy.querySelectorAll('video').forEach((v) => v.removeAttribute('src'));
        this.track.appendChild(copy);
      }
    }

    start() {
      if (this.raf || !this.drift) return;
      this.clone();
      this.last = 0;
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.track.querySelectorAll('video').forEach((v) => v.pause());
    }

    frame(now) {
      const dt = this.last ? Math.min(now - this.last, 64) : 0;
      this.last = now;
      if (!this.drag?.moved && !this.hover && !this.focused && !this.dialog.open && now > this.hold) this.offset += (SPEED * dt) / 1000;
      if (now - (this.checked || 0) > 200) {
        this.checked = now;
        this.clone(); // Horizon may re-render the block on a variant change
        this.measure();
        this.playVisible();
      }
      if (this.span) this.offset = ((this.offset % this.span) + this.span) % this.span;
      this.track.style.transform = `translate3d(${this.rtl ? this.offset : -this.offset}px, 0, 0)`;
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    measure() {
      const first = this.track.firstElementChild;
      const copy = this.track.querySelector('[data-rp-clone]');
      this.span = first && copy ? Math.abs(copy.offsetLeft - first.offsetLeft) : 0;
    }

    // only cards inside the strip play; a card's video is fetched the first time it comes into view
    playVisible() {
      const width = this.viewport.clientWidth;
      const shift = this.track.offsetLeft + (this.rtl ? this.offset : -this.offset);
      for (const li of this.track.children) {
        const video = li.querySelector('video');
        if (!video) continue;
        const left = li.offsetLeft + shift;
        const show = left + li.offsetWidth > 0 && left < width && !this.dialog.open && !document.hidden;
        if (show) {
          if (!video.getAttribute('src')) video.src = video.dataset.src;
          if (video.paused && !video.ended) video.play().catch(() => {});
          if (video.error && !video.dataset.retried) { // a dropped fetch on a flaky connection: one more try
            video.dataset.retried = '1';
            video.src = video.dataset.src;
            video.load();
          }
        } else if (!video.paused) {
          video.pause();
        }
      }
    }

    listen() {
      const vp = this.viewport;
      vp.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (this.hover = true));
      vp.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && (this.hover = false));
      this.addEventListener('focusin', (e) => (this.focused = e.target.matches(':focus-visible')));
      this.addEventListener('focusout', () => (this.focused = false));
      vp.addEventListener('pointerdown', (e) => {
        if (this.drift && e.button === 0) this.drag = { x: e.clientX, from: this.offset, id: e.pointerId, moved: false };
      });
      vp.addEventListener('pointermove', (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        const dx = e.clientX - d.x;
        if (!d.moved && Math.abs(dx) > 6) {
          d.moved = true;
          vp.setPointerCapture(e.pointerId);
        }
        if (d.moved) this.offset = d.from + (this.rtl ? dx : -dx);
      });
      const end = () => {
        if (this.drag?.moved) {
          this.dragged = true;
          setTimeout(() => (this.dragged = false), 60);
          this.hold = performance.now() + 2500;
        }
        this.drag = null;
      };
      vp.addEventListener('pointerup', end);
      vp.addEventListener('pointercancel', end);
      vp.addEventListener('click', (e) => this.dragged && (e.preventDefault(), e.stopPropagation()), true);

      this.addEventListener('click', (e) => {
        const card = e.target.closest('.rp-reel__card');
        if (card && this.track.contains(card)) {
          const li = card.parentElement;
          const n = this.originals().length;
          this.open([...this.track.children].indexOf(li) % n);
          return;
        }
        const step = e.target.closest('[data-rp-step]');
        if (step) this.show(this.index + Number(step.dataset.rpStep));
        else if (e.target.closest('[data-rp-close]') || e.target === this.dialog || e.target.classList.contains('rp-reel__stage')) this.dialog.close();
        else if (e.target.closest('[data-rp-buy]')) this.buy();
      });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') this.show(this.index + ((e.key === 'ArrowRight') !== this.rtl ? 1 : -1));
      });
      this.dialog.addEventListener('close', () => this.closed());
      this.big.addEventListener('ended', () => {
        this.plays += 1;
        if (this.plays < this.repeats()) {
          this.big.currentTime = 0;
          this.big.play().catch(() => {});
        } else {
          this.show(this.index + 1);
        }
      });
    }

    addToCart() {
      const scope = this.closest('.shopify-section') || document;
      return scope.querySelector('add-to-cart-component button[name="add"]');
    }

    repeats() {
      const d = this.big.duration;
      return d && isFinite(d) ? Math.max(1, Math.ceil(MIN_VIEW_S / d)) : 1;
    }

    open(i) {
      const bars = this.querySelector('.rp-reel__bars');
      bars.replaceChildren(...this.cards().map(() => document.createElement('i')));
      const button = this.addToCart();
      this.querySelector('[data-rp-buy]').hidden = !button || button.disabled;
      this.track.querySelectorAll('video').forEach((v) => v.pause());
      document.documentElement.classList.add('rp-reel-open');
      this.dialog.showModal();
      this.show(i);
      const tick = () => {
        if (!this.dialog.open) return;
        const d = this.big.duration;
        const p = d && isFinite(d) ? Math.min(1, (this.plays * d + this.big.currentTime) / (this.repeats() * d)) : 0;
        [...bars.children].forEach((bar, k) => bar.style.setProperty('--p', k < this.index ? 1 : k > this.index ? 0 : p));
        this.progress = requestAnimationFrame(tick);
      };
      this.progress = requestAnimationFrame(tick);
    }

    show(i) {
      const cards = this.cards();
      this.index = (i + cards.length) % cards.length;
      const card = cards[this.index];
      this.plays = 0;
      this.big.poster = card.dataset.poster;
      this.big.src = card.dataset.full || card.querySelector('video').dataset.src;
      this.querySelector('.rp-reel__big-caption').textContent = card.getAttribute('aria-label');
      this.big.play().catch(() => {});
    }

    closed() {
      cancelAnimationFrame(this.progress);
      this.big.pause();
      this.big.removeAttribute('src');
      this.big.load();
      document.documentElement.classList.remove('rp-reel-open');
      this.hold = performance.now() + 800;
    }

    buy() {
      const button = this.addToCart();
      this.dialog.close();
      if (button && !button.disabled) button.click();
    }
  }

  if (!customElements.get('rp-reel')) customElements.define('rp-reel', RpReel);
})();

// 5. product page review strip (blocks/rp-reviews.liquid) ---------------------------------------
// The cards drift sideways like the video strip (the list plus a copy of it, moved by a transform), faster, and stop while
// a finger or the mouse is on them; a drag scrolls the strip and it waits 2.5 s before drifting again; keyboard focus on the
// strip holds it. Under reduced motion nothing drifts and the strip scrolls by hand.
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class RpReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.viewport = this.querySelector('.rp-reviews__viewport');
      this.track = this.querySelector('.rp-reviews__track');
      if (!this.viewport || !this.track) return;
      this.ready = true;
      this.speed = Number(this.dataset.speed) || 44; // px per second
      this.offset = 0;
      this.hold = 0;
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.drift = !reduce.matches && this.speed > 0 && this.originals().length > 1;
      this.classList.toggle('rp-reviews--drift', this.drift);
      this.listen();
      this.seen = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.onScreen ? this.start() : this.stop();
      }, { rootMargin: '120px 0px' });
      this.seen.observe(this);
      this.onVisibility = () => (document.hidden ? this.stop() : this.onScreen && this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stop();
      this.seen?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.ready = false;
    }

    originals() {
      return [...this.track.children].filter((li) => !li.hasAttribute('data-rp-clone'));
    }

    clone() {
      if (!this.drift || this.track.querySelector('[data-rp-clone]')) return;
      for (const li of this.originals()) {
        const copy = li.cloneNode(true);
        copy.setAttribute('data-rp-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        this.track.appendChild(copy);
      }
    }

    start() {
      if (this.raf || !this.drift) return;
      this.clone();
      this.last = 0;
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    frame(now) {
      const dt = this.last ? Math.min(now - this.last, 64) : 0;
      this.last = now;
      const still = this.drag?.moved || this.touching || this.hover || this.focused || now < this.hold;
      if (!still) this.offset += (this.speed * dt) / 1000;
      if (now - (this.checked || 0) > 250) {
        this.checked = now;
        this.clone(); // Horizon may re-render the block on a variant change
        this.measure();
      }
      if (this.span) this.offset = ((this.offset % this.span) + this.span) % this.span;
      this.track.style.transform = `translate3d(${this.rtl ? this.offset : -this.offset}px, 0, 0)`;
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    measure() {
      const first = this.track.firstElementChild;
      const copy = this.track.querySelector('[data-rp-clone]');
      this.span = first && copy ? Math.abs(copy.offsetLeft - first.offsetLeft) : 0;
    }

    listen() {
      const vp = this.viewport;
      vp.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (this.hover = true));
      vp.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && (this.hover = false));
      // only keyboard focus holds the strip: a tap on a phone focuses the strip too, and that must not keep it frozen
      vp.addEventListener('focus', () => (this.focused = vp.matches(':focus-visible')));
      vp.addEventListener('blur', () => (this.focused = false));
      vp.addEventListener('pointerdown', (e) => {
        if (!this.drift || e.button !== 0) return;
        this.touching = true;
        this.drag = { x: e.clientX, from: this.offset, id: e.pointerId, moved: false };
      });
      vp.addEventListener('pointermove', (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        const dx = e.clientX - d.x;
        if (!d.moved && Math.abs(dx) > 12) { // a thumb wobbles a few px during a tap: that is still a tap
          d.moved = true;
          vp.setPointerCapture(e.pointerId);
        }
        if (d.moved) this.offset = d.from + (this.rtl ? dx : -dx);
      });
      vp.addEventListener('pointerup', (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        this.touching = false;
        this.drag = null;
        if (d.moved) this.hold = performance.now() + 2500;
      });
      vp.addEventListener('lostpointercapture', () => {
        this.touching = false; // the browser took the pointer (a long press, a system gesture): never stay frozen
        this.drag = null;
      });
      vp.addEventListener('pointercancel', (e) => {
        if (this.drag && e.pointerId === this.drag.id) { // the page scrolled vertically instead: not a tap
          this.touching = false;
          this.drag = null;
        }
      });
    }
  }

  if (!customElements.get('rp-reviews')) customElements.define('rp-reviews', RpReviews);
})();
